import React, { useEffect, useState } from 'react';
import { IParent } from '../../components/common/types';
import useGames, { emptyGame, IGame, State } from '../Games/useGames';
import { IContent } from '../../pages/HomePage/Featured/featured';
import { useEffectOnce } from 'usehooks-ts';

interface IFeatureProvider {
	games: IGame[];
	current: IContent;
	setFeatured: (target: IGame) => void;
	isLoading: boolean;
}

export const emptyFeature: IContent = {
	game: emptyGame,
	description: '',
	discount: '',
	image: '',
};

export const emptyFeatureProvider: IFeatureProvider = {
	current: emptyFeature,
	games: [],
	setFeatured(target: IGame): void {},
	isLoading: false,
};

export const FeatureProvider = ({ children }: IParent) => {
	const [featured, setFeatured] = useState(emptyFeature);
	const [isLoading, setIsLoading] = useState(true);
	/** @dev Feature never loads as its hard-coded */

	const games = useGames();

	const handleSetFeatured = (target: IGame) => {
		const featureData = features.find(element => element.address === target.address);

		if (!featureData) {
			setFeatured({
				...emptyFeature,
				game: target,
			});

			return;
		}

		const { address: _, ...strippedFeature } = featureData;
		setFeatured({
			...strippedFeature,
			game: target,
		});
	};

	useEffectOnce(() => {
		games.fetch(features.map(feature => feature.address));
	});

	useEffect(() => setIsLoading(games.state === State.LOADING || !games.data.length), [games.state, games.data]);

	/** Set up first data */
	useEffect(() => {
		if (isLoading || !games.data.length /** There's always at least one promotion */) {
			return;
		}

		handleSetFeatured(games.data[0]);
	}, [isLoading, games.data]);

	return (
		<FeatureContext.Provider
			value={{ current: featured, games: games.data, setFeatured: handleSetFeatured, isLoading: isLoading }}
		>
			{children}
		</FeatureContext.Provider>
	);
};

const features: ReadonlyArray<{ address: string; description?: string; discount?: string; image: string }> = [
	{
		address: '0x29790a7858015072f94c03e5761b98d7fe4de9a3',
		description:
			'Cyberpunk 2077 is an open-world, action-adventure RPG set in the megalopolis of Night City, where you play as a cyberpunk mercenary wrapped up in a do-or-die fight for survival. Improved and featuring all-new free additional content, customize your character and playstyle as you take on jobs, build a reputation, and unlock upgrades.',
		image: '/assets/images/featured/header/cyberpunk.jpeg',
	},
	// {
	// 	address: '0xbac85939aed35d518283cc73d4f303cfd35d8ff0',
	// 	description:
	// 		'Play as Gregor, turned into a tiny bug, and set out on an extraordinary journey to unravel the mystery of your transformation. Metamorphosis is a first person adventure set in a surrealist world where your newfound abilities are your last and only hope for redemption.',
	// 	image: '/assets/images/featured/header/metamorphosis.jpg',
	// },
	{
		address: '0x9b670efbe409834fa631588b0c75df403ec4333e',
		description:
			'Desperate, outnumbered and with gun turrets as your only companion. Lumencraft is a top-down shooter with base-building elements.',
		image: '/assets/images/featured/header/lumencraft.jpg',
	},
	{
		address: '0x9ec4a3eff8ccaf295321aa4c2a03552f11c760af',
		description:
			'Start your adventure in a magic Cryptoland that needs saving from evil forces that all want to use crypto for bad purposes.',
		image: '/assets/images/featured/header/cryptominer.jpg',
	},
	{
		address: '0xd037102c5bc71e576fe9c0b1b8398e58be1e4ef7',
		discount: '1000000',
		image: '/assets/images/featured/header/cryptowars.jpg',
	},
	{
		address: '0xda40b36dda5c38d4d364a2e0c6d9730f3dd1a473',
		image: '/assets/images/featured/header/toastit.jpg',
	},
] as const;

const FeatureContext = React.createContext<IFeatureProvider>(emptyFeatureProvider);

export const useFeatureProvider = () => {
	return React.useContext(FeatureContext);
};
